import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBreadcrumbs, useUser } from '../hooks';
import { addCommas, bindClick, getAssetTypeIcon } from '../SiteUtil';
import { Header, Groupper, Table, Icon, Message, Button, Toolbar, Modal, Toast } from 'react-frontier';
import { WorkerModal, BankAccountModal, AddAssetModal, AssetViewModal, WorkerRatings } from '../components';
import { Worker, SetLoading, BankAccount, Asset, AssetAddOptions, WorkerBanReason, UserAssetType } from '../SiteClasses';
import { Dropdown, Label } from 'semantic-ui-react';
import classNames from 'classnames';
import API from '../API';
import moment from 'moment';
import UserAccess from '../UserAccess';

interface WorkerViewProps{
	
}

enum Modals{
	CREATE_ASSET = 1,
	VIEW_ASSET = 2,
	DELETE_ASSET = 3,
	BAN_WORKER = 4,
	UNBAN_WORKER = 5,
	VIEW_IMAGE = 6,
	DELETE_IMAGE = 7,
	VALIDATE_RFC = 8,
}

var WorkerView = (props: WorkerViewProps)=>{
	var params = useParams<{ wid: string }>();
	var { hasAccess, user } = useUser();
	var { setTitle } = useBreadcrumbs();
	var imageInputRef = React.useRef<HTMLInputElement>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [workerData, setWorkerData] = useState<Worker>(null);
	var [workerModal, setWorkerModal] = useState<boolean>(false);
	var [deleteModal, setDeleteModal] = useState<boolean>(false);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [addBankModal, setAddBankModal] = useState<boolean>(false);
	var [selectedAsset, setSelectedAsset] = useState<Asset>(null);
	var [shownModal, setShownModal] = useState<number>(null);
	var [banReasons, setBanReasons] = useState<WorkerBanReason[]>([]);
	var [selectedBanReason, setSelectedBanReason] = useState<number>(null);
	var [addingImage, setAddingImage] = useState<boolean>(false);   
	
	useEffect(()=>{
		setTitle('Trabajador #'+parseInt(params.wid));
		API.getWorker(parseInt(params.wid)).then(res=>{
			if(res.error) setLoadError(res.message);
			if(res.data?.zipcode){
				res.data.zipcode = ('00000' + res.data.zipcode).slice(-5);
			}
			setWorkerData(res.data);
		}).catch(err=>{
			setLoadError('Hubo un error cargando el trabajador (LCL-1)');
		})
	}, []);

	var onShowBanModal = ()=>{
		if(banReasons.length === 0){
			API.getBanReasons(100, 0).then(res=>{
				if(!res.error) setBanReasons(res.data);
			});
		}
		setShownModal(Modals.BAN_WORKER);
	}

	var onEdit =(w: Worker)=>{
		setWorkerData(w);
		setWorkerModal(false);
	}

	var onDelete = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteWorker(workerData.worker_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Trabajador eliminado!');

			workerData.deleted = true;
			setWorkerData(workerData);
			setDeleteModal(false);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado eliminando el trabajador. (LCL-1)']);
		}).finally(()=>{	
			setLoading(false);
		})
	}

	var addBankAccount = (b: BankAccount)=>{
		var w =  {...workerData};
		w.account = b;
		w.account_id = b.account_id;
		setWorkerData(w);
		setAddBankModal(false);
	}

	var onAssetAdded = (asset: Asset, options: AssetAddOptions)=>{
		var w = { ...workerData };
		if(!w.assets) w.assets = [];
		w.assets.push({
			...asset,
			user_asset_type: options.user_asset_type,
			user_asset_type_name: options.user_asst_type_name,
		});
		if(options.user_asset_type === UserAssetType.CIF){
			w.cif_asset_id = asset.asset_id;
		}
		setWorkerData(w);
	};

	var onAssetDeleted = (asset_id: number)=>{
		var w = { ...workerData };
		var deletedAsset = w.assets.find(asset => asset.asset_id === asset_id);
		w.assets = w.assets.filter(asset => asset.asset_id !== asset_id);
		
		if(deletedAsset?.user_asset_type === UserAssetType.CIF && w.cif_asset_id === asset_id){
			w.cif_asset_id = null;
		}
		
		setWorkerData(w);
	};

	var onBanAction = (setLoading: SetLoading)=>{
		var isBanning = shownModal === Modals.BAN_WORKER;
		if(isBanning && !selectedBanReason) return setModalPrompts(['Debe seleccionar un motivo de inhabilitación']);
		
		setLoading(true);
		API.banWorker(workerData.worker_id, isBanning ? selectedBanReason : null).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success(isBanning ? 'Trabajador inhabilitado correctamente' : 'Trabajador habilitado correctamente');

			var w = { ...workerData };
			if(isBanning){
				var reason = banReasons.find(r => r.reason_id === selectedBanReason);
				w.ban_reason = selectedBanReason;
				w.ban_reason_name = reason?.reason_name;
				w.user_banned = user.user_id;
				w.user_banned_name = user.name;
				w.date_banned = moment().unix();
			}else{
				w.ban_reason = null;
				w.ban_reason_name = null;
				w.user_banned = null;
				w.user_banned_name = null;
				w.date_banned = null;
			}
			
			setWorkerData(w);
			setShownModal(null);
			setSelectedBanReason(null);
		}).catch(err=>{
			setModalPrompts([`Hubo un error inesperado al ${isBanning ? 'inhabilitar' : 'habilitar'} el trabajador. (LCL-BAN-1)`]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var validateRFC = (setLoading: SetLoading)=>{
		setLoading(true);
		API.validateWorkerRFC([workerData.worker_id]).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('RFC validado correctamente');
			
			var w = {...workerData};
			w.valid_rfc = true;
			setWorkerData(w);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado validando el RFC. (LCL-VRF-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var addImage = ()=>{
		imageInputRef.current?.click();
	}

	var handleImage = (ev: React.ChangeEvent<HTMLInputElement>)=>{
		if(!ev.target.files || ev.target.files.length==0) return;
		if(ev.target.files.length>1) return Toast.error("Favor de solo seleccionar 1 archivo.");
		var image = ev.target.files[0];
		if(image.size>(2.5*1024*1024)) return Toast.error('La imagen no puede pesar mas de 2.5MB.');

		setAddingImage(true);
		API.addWorkerImage(workerData.worker_id, image).then(res=>{
			if(res.error) return Toast.error(res.message);
			
			var w = {...workerData};
			w.image = res.data.image_url;
			setWorkerData(w);

			Toast.success('Se ha agregado la imagen del trabajador');
		}).catch(err=>{
			return Toast.error('Hubo un error subiendo la imagen del trabajador. (LCL-1)');
		}).finally(()=>{
			setAddingImage(false);
		});
	}

	var viewImage = ()=>{
		setShownModal(Modals.VIEW_IMAGE);
	}

	var deleteImage = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteWorkerImage(workerData.worker_id).then(res=>{
			if(res.error) return Toast.error(res.message);

			var w = {...workerData};
			w.image = res.data.image;
			setWorkerData(w);
			Toast.success('Se ha eliminado la imagen del trabajador.');
			setShownModal(null);
		}).catch(err=>{
			return Toast.error('Hubo un error inesperado eliminando la imagen. (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var worker_onboarded = useMemo(()=>{
		if(!workerData) return false;
		return !!workerData.account_id && !!workerData.zipcode;
	}, [workerData]);

	var downloadPayments = ()=>{
		API.downloadWorkerPaymentsReport(`${workerData.first_name} ${workerData.last_name}`, workerData.worker_id).then(res=>{
			if(res.error) return Toast.error(res.message);
		}).catch(()=>{
			Toast.error('Hubo un error descargando los pagos (WV-PAY-1)');
		});
	};

	if(loadError) return <Header text='Error' subtext={loadError} />
	if(!workerData) return <Header text='Cargando datos' loading />

	return <div className='op worker-view-screen'>
		{workerData.ban_reason && 
			<Message type='error' centered style={{ maxWidth: 500, margin: 'auto', marginTop: 15 }}>
				<div className="header">Trabajador inhabilitado</div>
				<p style={{ marginTop: 10 }}>
					Este trabajador fue inhabilitado el {moment.unix(workerData.date_banned).format('DD/MM/YYYY hh:ss')} por <b>{workerData.user_banned_name}</b>.
				</p>
				<p style={{ marginTop: 5 }}>
					<strong>Motivo:</strong> {workerData.ban_reason_name}
				</p>
			</Message>
		}
		{!worker_onboarded && 
			<Message type='error' centered style={{ maxWidth: 500 , margin: 'auto', marginTop: 15}}>
				<div className="header">Información incompleta</div>
				{!workerData.account_id && (
					<div>- No se ha asignado una cuenta bancaria</div>
				)}
				{!workerData.zipcode && (
					<div>- No se ha asignado la ciudad del trabajador</div>
				)}
			</Message>
		}
		<Groupper title={'Información de trabajador'} width={500} style={{ marginTop: 15 }} fitted>
			<Toolbar>
				<Toolbar.Item text='Editar' iconName='pencil' onClick={bindClick(setWorkerModal, true)}/>
				<Toolbar.Item text='Pagos' iconName='money-bill' onClick={downloadPayments}/>
				{!workerData.deleted && (
					<Toolbar.Item text='Eliminar' iconName='close' onClick={bindClick(setDeleteModal, true)}/>
				)}
				{hasAccess(UserAccess.WORKERS.VALIDATE_RFC) && !workerData.deleted && !workerData.valid_rfc && workerData.cif_asset_id ? (
					<Toolbar.Item text='Validar RFC' iconName='check' onClick={bindClick(setShownModal, Modals.VALIDATE_RFC)}/>
				) : null }
				{hasAccess(UserAccess.WORKERS.UNBAN_WORKERS) && !workerData.deleted && !!workerData.ban_reason && (
					<Toolbar.Item text='Habilitar' iconName='unlock' onClick={bindClick(setShownModal, Modals.UNBAN_WORKER)}/>
				)}
				{hasAccess(UserAccess.WORKERS.BAN_WORKERS) && !workerData.deleted && !workerData.ban_reason && (
					<Toolbar.Item text='Inhabilitar' iconName='ban' onClick={onShowBanModal}/>
				)}
			</Toolbar>
			{hasAccess(UserAccess.WORKERS.EDIT_WORKERS) && (
				<input
					type="file"
					style={{ display: 'none' }}
					ref={imageInputRef}
					accept="image/*"
					onChange={handleImage}
				/>
			)}
			<div className="image">
				{workerData.image ? (
					<img
						src={workerData.image}
						className="image"
						onClick={viewImage}
					/>
				) : hasAccess(UserAccess.WORKERS.EDIT_WORKERS) ? (
					<div onClick={addImage} className={classNames('placeholder', {
						loading: addingImage,
					})}>
						{addingImage ? (
							<div className="fr loading inline"></div>
						) : (
							<Icon name="plus" />
						)}
					</div>
				) : (
					<div className="static">
						<Icon name='user' />
					</div>
				)}
			</div>
			{}
			<Groupper.Divider text={'Detalles'} />
			<Table
				details
				striped
				data={[
					['ID', workerData.worker_id],
					['Nombre', (workerData.first_name + ' ' +workerData.last_name)],
					['Calificación',  <>
						<Icon name='star' style={{ color: workerData.rating ? '#f2b01e' : 'gray' }}/>
						{workerData.rating || <Icon name='minus' color='gray' />}
					</>],
					['Correo electrónico', workerData.email],
					['Teléfono', workerData.phone],
					['RFC', <>{workerData.rfc} <div style={{ marginLeft: 10 }} className={classNames("fr label", {
						green: workerData.valid_rfc,
						red: !workerData.valid_rfc
					})}>
						{workerData.valid_rfc ? 'Validado' : 'No validado'}
					</div></>],
					['CURP', workerData.curp],
					['Código postal', workerData.zipcode || <Icon name='minus' />],
					['Estatus', <div className={classNames("fr label", {
						green: !workerData.deleted && worker_onboarded && !workerData.ban_reason,
						red: workerData.deleted || !worker_onboarded || workerData.ban_reason
					})}>
						{workerData.deleted ? 'Eliminado' : workerData.ban_reason ? 'Inhabilitado' : (!worker_onboarded ? 'No completado' : 'Activo')}
					</div>],
				]}
			/>
		</Groupper>
		{workerData.comments ? <Groupper title={'Comentarios'} width={500} style={{ marginTop: 15 }}>
			<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
				{workerData.comments}
			</div>
		</Groupper> : null}
		{workerData.account ? (
			<Table details striped
				title='Cuenta Bancaria'
				style={{ maxWidth: 500, margin: 'auto', marginTop: 15 }}
				button={(
					<Button text='Editar' iconName='pen' onClick={bindClick(setAddBankModal, true)} size='tiny' />
				)}
				data={[
					
					['Propietario', workerData.account.name_account],
					
					['RFC', workerData.account.rfc],
					['Código postal', workerData.account.postal_code],
					null,
					['Tipo de cuenta', workerData.account.card_number ? <><Icon name='credit-card'/>Tarjeta bancaria</> : <><Icon name='landmark'/>Transferencia bancaria (SPEI)</>],
					['Banco', <>
						{workerData.account.bank}
						<div className="meta">P: {workerData.account.bank_participant}</div>
					</>],
					[workerData.account.card_number ?  'Tarjeta Bancaria' : 'CLABE', workerData.account.card_number ? workerData.account.card_number : workerData.account.clabe],
				]}
			/>
		) : (
			<Groupper width={500} style={{ marginTop: 15 }} title={'Cuenta Bancaria'} titleRight={(
				<Button text='Editar' iconName='pen' style={{ fontSize: 12, minWidth: 100 }}  onClick={bindClick(setAddBankModal, true)}/>
			)}>
				<Header text={'Sin cuenta bancaria'} subtext={'El trabajador no tiene una cuenta bancaria asignada'} style={{ minHeight: 150, alignItems: 'center', display: 'flex', justifyContent: 'center' }} />
			</Groupper>
		)}
		<WorkerRatings style={{ marginTop: 15 }} workerId={workerData.worker_id}/>
		<Table 
			fitted 
			striped
			title='Archivos'
			headers={['ID', 'Nombre', 'Tipo', <Icon name='wrench' />]}
			style={{ width: 500, margin: 'auto', marginTop: 15 }}
			button={(
				<Button text="Agregar" color="black" iconName="plus-circle" size="small" onClick={bindClick(setShownModal, Modals.CREATE_ASSET)} />
			)}
		>
			{workerData.assets && workerData.assets.length !== 0 ? (
				workerData.assets.map(a => (
					<Table.Row
						key={a.asset_id}
						collapsingIndexes={[0, 2, 3, 4]}
						data={[
							a.asset_id,
							a.asset_name,
							<>
								<Icon name={getAssetTypeIcon(a.type_id)} />
								{a.user_asset_type_name}
							</>,
							<Dropdown icon={(
								<div className="fr label" style={{ padding: '3px 10px' }}>
									<Icon name='ellipsis' style={{ margin: 0 }} />
								</div>
								)} className='icon'>
									<Dropdown.Menu>
										<Dropdown.Item onClick={() => { setSelectedAsset(a); setShownModal(Modals.VIEW_ASSET); }}>
											<Icon name="eye" style={{ marginRight: 3 }} /> Ver 
										</Dropdown.Item>
										<Dropdown.Item onClick={() => { setSelectedAsset(a); setShownModal(Modals.DELETE_ASSET); }}>
											<Icon name="close" style={{ marginRight: 3 }} /> Eliminar 
										</Dropdown.Item>
									</Dropdown.Menu>
							</Dropdown>
						]}
					/>
				))
			) : (
				<Table.Cell row className='empty' colSpan={6}>
					El usuario no tiene archivos
				</Table.Cell>
			)}
		</Table>
		<Table striped
			title='Eventos'
			footer={workerData.events.length !== 0 &&
				<Table.Row>
					<Table.Cell colSpan={5} style={{ textAlign: 'right', fontSize: 14, fontWeight: 'bold' }}>
						Total:
					</Table.Cell>
					<Table.Cell collapsing colSpan={1} style={{ textAlign: 'start', fontSize: 14, fontWeight: 'bold' }}>
						${addCommas(workerData.events.reduce((acc, e) => acc + e.payout_amount, 0))}
					</Table.Cell>
				</Table.Row>
			}
			headers={['Evento', 'Fecha', 'Nombre', 'Coordinador', 'Puesto', 'Sueldo']}
			selectable
			collapsingIndexes={[0, 1, 3, 5]}
			style={{ maxWidth: 800, margin: 'auto', marginTop: 15, marginBottom: 15 }}
		>
			{workerData.events.length !== 0 ? (
				workerData.events.map(e=>(
					<Table.Row 
						compact
						key={e.date_id}
						collapsingIndexes={[0, 2, 3]}
						as={Link}
						to={'/events/'+e.date_id}
						data={[
							e.date_id,
							moment.unix(e.date).format('DD/MM/YY'),
							e.event_name,
							e.coordinator_name,
							e.title_name,
							'$'+addCommas(e.payout_amount)
						]}
					/>
				))
			) : (
				<Table.Cell row className='empty noselect' colSpan={8}>
					Este trabajador no tiene eventos.
				</Table.Cell>
			)}
		</Table>
		<WorkerModal open={workerModal} onClose={bindClick(setWorkerModal, false)} onUpdate={(w) => onEdit(w)} workerToEdit={workerData}/>
		<Modal open={deleteModal} onClose={bindClick(setDeleteModal, false)} size='tiny' >
			<Modal.Header>¿Eliminar trabajador?</Modal.Header>
			{workerData ? 
				<Modal.Content>
					<Header text='¿Eliminar al trabajador?' subtext={'El trabajador sera eliminado dentro del sistema'}/>
					<Message type='error' centered>
						Se eliminará el trabajador de todos los puestos a los cuales esté asignado
					</Message>
					<Message type="error" list={modalPrompts} />
				</Modal.Content> 
			: null}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setDeleteModal, false)} />
				<Button text='Eliminar' color='red' onClick={onDelete}/>
			</Modal.Actions>
		</Modal>
		<BankAccountModal
			worker_id={workerData.worker_id}
			open={addBankModal}
			onClose={bindClick(setAddBankModal, false)}
			onCreate={(b) => addBankAccount(b)}
		/>
		<AssetViewModal
			open={shownModal === Modals.VIEW_ASSET}
			onClose={bindClick(setShownModal, null)}
			asset={selectedAsset}
		/>
		<AssetViewModal
			open={shownModal === Modals.DELETE_ASSET}
			onClose={bindClick(setShownModal, null)}
			asset={selectedAsset}
			delete={true}
			onDeleted={onAssetDeleted}
		/>
		<AddAssetModal
			open={shownModal === Modals.CREATE_ASSET}
			onClose={bindClick(setShownModal, null)}
			workerId={workerData.worker_id}
			onAdded={onAssetAdded}
		/>
		<Modal open={shownModal === Modals.BAN_WORKER || shownModal === Modals.UNBAN_WORKER} onClose={bindClick(setShownModal, null)} size='mini' >
			<Modal.Header>{shownModal === Modals.BAN_WORKER ? 'Inhabilitar trabajador' : 'Habilitar trabajador'}</Modal.Header>
			<Modal.Content>
				<Header text={shownModal === Modals.BAN_WORKER ? 'Seleccione el motivo de inhabilitación' : '¿Desea habilitar al trabajador?'} subtext={shownModal === Modals.BAN_WORKER ? 'El trabajador será inhabilitado y no podrá ser asignado a eventos' : 'El trabajador será habilitado y podrá ser asignado a eventos nuevamente'}/>
				{shownModal === Modals.BAN_WORKER && (
					<div style={{ marginTop: 15 }}>
						<label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>Motivo de inhabilitación</label>
						<Dropdown placeholder='Seleccione un motivo' fluid selection value={selectedBanReason} onChange={(e, { value }) => setSelectedBanReason(value as number)} options={banReasons.map(r => ({ key: r.reason_id, text: r.reason_name, value: r.reason_id }))}/>
					</div>
				)}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal, null)} />
				<Button text={shownModal === Modals.BAN_WORKER ? 'Inhabilitar' : 'Habilitar'} color={shownModal === Modals.BAN_WORKER ? 'red' : 'green'} onClick={onBanAction}/>
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.VIEW_IMAGE} size="mini" onClose={bindClick(setShownModal, null)} >
			<Modal.Header>Imagen</Modal.Header>
			<Modal.Content>
				<img src={workerData?.image} style={{ width: '100%', maxWidth: 400, display: 'block', margin: 'auto' }}/>
			</Modal.Content>
			<Modal.Actions>
				{hasAccess(UserAccess.WORKERS.EDIT_WORKERS) && (
					<Button onClick={bindClick(setShownModal, Modals.DELETE_IMAGE)} color="red" text="Eliminar"/>
				)}
				<Button onClick={bindClick(setShownModal, null)} text="Cerrar" />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.DELETE_IMAGE} size="mini" onClose={bindClick(setShownModal, null)} >
			<Modal.Header>Eliminar imagen</Modal.Header>
			<Modal.Content>
				<div className="op worker portrait single images" style={{ justifyContent: 'center' }}>
					<img src={workerData?.image} className="image" />
				</div>
				<Header text="¿Eliminar imagen?" style={{ marginTop: 15, marginBottom: 0 }}/>
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={bindClick(setShownModal, Modals.VIEW_IMAGE)} text="Cancelar" basic/>
				{hasAccess(UserAccess.WORKERS.EDIT_WORKERS) && (
					<Button onClick={deleteImage} color="red" text="Eliminar"/>
				)}
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.VALIDATE_RFC} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header>Validar RFC</Modal.Header>
			<Modal.Content>
				<Header text={`¿Marcar el RFC como válido?`} />
				{workerData && (
					<Table
						details
						data={[
							['ID', workerData.worker_id],
							['Nombre', `${workerData.first_name} ${workerData.last_name}`],
							['RFC', workerData.rfc]
						]}
					/>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' onClick={validateRFC} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default WorkerView;